import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';
import { AdmissionCareDayService } from 'app/um/services/admission-care-day-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class ManageDischargeLocationsPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected dischargeLocations: ReadonlyArray<UM.DischargeLocation> | null = null;

    protected editedDischargeLocation: UM.DischargeLocation | null = null;
    protected validate = validate;
    protected isValid: boolean = false;

    private sorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $dischargeLocationModal!: IPopup;
    protected $dischargeLocationForm!: IForm;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AdmissionCareDayService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected handleDischargeLocationFormSuccess() {
        this.closeDischargeLocationModal();
        this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadDischargeLocations();
    }

    protected closeDischargeLocationModal(): void {
        if(this.$dischargeLocationForm.reset) {
            this.$dischargeLocationForm.reset();
        }
        this.$dischargeLocationModal.close();
    }

    protected saveDischargeLocation(dischargeLocation: UM.DischargeLocation | null): void {
        this.editedDischargeLocation = dischargeLocation;
        this.$dischargeLocationModal.open();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async activateDischargeLocation(dischargeLocation: UM.DischargeLocation): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(dischargeLocation.isActive) {
            const result = await confirmation.showDeactivate('um:auth.discharge.location.$', 1);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateDischargeLocation(dischargeLocation.id!, !dischargeLocation.isActive);
            isSuccessful = response.isSuccessful;
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('um:auth.discharge.location.$', !dischargeLocation.isActive, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadDischargeLocations(): Promise<void> {
        const includes: string[] = [];

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadDischargeLocations(options, includes);
            this.dischargeLocations = response.results;
            this.resultCount = response.count;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
